<template>
  <div ui-view="" class="app-body" id="view">
    <page-header title="작업허가신청" description="작업허가 신청을 진행합니다."/>
    <div class="page-body ph-30">
      <div class="sticky-holder" style="position: relative; height: auto;">
        <div class="control-group space-between sticky" style="left: 0px; top: 0px; z-index: 100; position: static;">
          <div style="width:100%">
            <ul class="nav nav-step">
              <li v-for="(item, index) in steps" :key="index" :class="[item.isSelect]">
                <i>{{ item.id }}</i>{{ item.name }}
              </li>
            </ul>
          </div>
          <!-- 신청(약관/입력) 화면 한정: 세션(토큰) 남은시간 + 연장 -->
          <div v-if="showTimer" class="session-timer" style="display:flex;align-items:center;white-space:nowrap;">
            <span :class="{ 'text-danger': remainingMs <= 300000 }">
              세션 남은 시간 <b>{{ remainingText }}</b>
            </span>
            <button class="btn btn-sm line-1 ml-10" @click="extendSession" :disabled="extending">연장</button>
          </div>
        </div>
      </div>
      <keep-alive>
        <router-view/>
      </keep-alive>
    </div>

    <!-- 만료 임박(5분 이하) 연장 안내 -->
    <div v-if="showExtendModal" class="session-extend-dim"
         style="position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:2000;display:flex;align-items:center;justify-content:center;">
      <div style="background:#fff;border-radius:8px;padding:24px 28px;max-width:360px;width:90%;text-align:center;">
        <p style="margin:0 0 8px;font-weight:600;">세션이 곧 만료됩니다</p>
        <p style="margin:0 0 18px;color:#666;">남은 시간 <b>{{ remainingText }}</b><br>연장하시겠습니까?</p>
        <div style="display:flex;gap:8px;justify-content:center;">
          <button class="btn btn-m line-2" @click="dismissExtend">닫기</button>
          <button class="btn btn-m blue" @click="extendSession" :disabled="extending">연장하기</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/layout/PageHeader.vue';

const WARN_MS = 5 * 60 * 1000; // 5분

export default {
  components: {
    PageHeader
  },
  data() {
    return {
      steps: [
        { id: 0, name: '약관동의', routeName: 'workPermitAgree', isSelect: '' },
        { id: 1, name: '안전작업계획 입력', routeName: 'workPermitInput', isSelect: '' },
        { id: 2, name: '안전작업계획 확인', routeName: 'workPermitCheck', isSelect: '' }
      ],
      remainingMs: 0,
      timer: null,
      extending: false,
      showExtendModal: false,
      extendPrompted: false
    };
  },
  computed: {
    // 신청 흐름(약관/입력)에서만 타이머 노출
    isAgreeOrInput() {
      return this.$route.name === 'workPermitAgree' || this.$route.name === 'workPermitInput';
    },
    showTimer() {
      return this.isAgreeOrInput && this.remainingMs > 0;
    },
    remainingText() {
      let s = Math.max(0, Math.floor(this.remainingMs / 1000));
      const h = Math.floor(s / 3600); s -= h * 3600;
      const m = Math.floor(s / 60); s -= m * 60;
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(h)}:${pad(m)}:${pad(s)}`;
    }
  },
  mounted() {
    this.setStep();
    this.tick();
    this.timer = setInterval(this.tick, 1000);
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    /** JWT exp(초) 디코드 → 만료 시각(ms). 실패 시 0. */
    tokenExpMs() {
      const token = this.$store.getters['auth/data'];
      if (!token || String(token).split('.').length < 2) return 0;
      try {
        const b64 = String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(decodeURIComponent(escape(atob(b64))));
        return payload.exp ? payload.exp * 1000 : 0;
      } catch (e) {
        return 0;
      }
    },
    tick() {
      if (!this.isAgreeOrInput) { this.remainingMs = 0; return; }
      const exp = this.tokenExpMs();
      this.remainingMs = exp ? Math.max(0, exp - Date.now()) : 0;
      // 5분 이하 & 아직 안내 안 함 → 연장 모달
      if (this.remainingMs > 0 && this.remainingMs <= WARN_MS && !this.extendPrompted && !this.showExtendModal) {
        this.showExtendModal = true;
        this.extendPrompted = true;
      }
    },
    async extendSession() {
      if (this.extending) return;
      this.extending = true;
      const token = this.$store.getters['auth/data'];
      try {
        const res = await this.$http.post('/api/public/token/refresh', null, {
          headers: { 'X-AUTH-TOKEN': token }
        });
        const newToken = res && res.data ? res.data.token : null;
        if (newToken) {
          this.$store.commit('auth/setAuth', newToken);
          this.showExtendModal = false;
          this.extendPrompted = false; // 다음 임박 시 다시 안내
          this.tick();
          if (this.noti) this.noti('세션 연장', '세션이 연장되었습니다.', 'success');
        } else {
          throw new Error('no token');
        }
      } catch (e) {
        console.log('세션 연장 실패', e);
        if (this.noti) this.noti('연장 실패', '세션 연장에 실패했습니다. 다시 인증해 주세요.', 'danger');
      } finally {
        this.extending = false;
      }
    },
    dismissExtend() {
      this.showExtendModal = false;
    },
    setStep() {
      let selectIndex = 0;
      this.steps.forEach((item) => {
        if (this.$route.name === item.routeName) {
          item.isSelect = 'on';
          selectIndex = item.id;
        } else {
          item.isSelect = '';
        }
      });
      const checkedSteps = this.steps.slice(0, selectIndex);
      checkedSteps.forEach((item) => {
        item.isSelect = 'checked';
      });
    }
  },
  watch: {
    $route() {
      this.setStep();
      this.tick();
    }
  }
};
</script>
